import React, { useState } from 'react';

/**
 * AI Coach Insights Component
 * Displays explanatory feedback for AI Score with improvement tips
 * Updated with EMZI CI colors and improved readability
 */
const AICoachInsight = ({ coachInsights, aiScore }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!coachInsights) {
    return null;
  }

  const { summary, strengths, improvements, encouragement } = coachInsights;

  // EMZI Brand colors with score-based accents
  const getThemeColors = () => {
    if (aiScore >= 80) {
      return {
        primary: '#10B981',      // Green
        background: '#ECFDF5',   // Light green bg
        text: '#065F46',         // Dark green text
        badge: '#10B981'
      };
    } else if (aiScore >= 60) {
      return {
        primary: '#F7941E',      // EMZI Orange
        background: '#FFF7ED',   // Light orange bg
        text: '#7C2D12',         // Dark orange text
        badge: '#F7941E'
      };
    } else {
      return {
        primary: '#EF4444',      // Red
        background: '#FEF2F2',   // Light red bg
        text: '#991B1B',         // Dark red text
        badge: '#EF4444'
      };
    }
  };

  const theme = getThemeColors();

  return (
    <div style={{
      marginTop: '12px',
      border: `2px solid ${theme.primary}`,
      borderRadius: '12px',
      background: '#FFFFFF',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      transition: 'all 0.3s ease'
    }}>
      {/* Header - Always visible */}
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          padding: '14px 16px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: theme.background,
          borderBottom: isExpanded ? `1px solid ${theme.primary}30` : 'none',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.opacity = '0.9';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = '1';
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px' }}>💡</span>
          <span style={{
            fontSize: '15px',
            fontWeight: 700,
            color: theme.text,
            letterSpacing: '0.3px'
          }}>
            AI Coach Insights
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{
            fontSize: '12px',
            fontWeight: 600,
            color: theme.text,
            opacity: 0.7
          }}>
            {isExpanded ? 'Hide' : 'Show'}
          </span>
          <span style={{
            fontSize: '14px',
            color: theme.text,
            transition: 'transform 0.2s ease',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
            display: 'inline-block'
          }}>
            ▼
          </span>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={{
          padding: '16px',
          fontSize: '14px',
          lineHeight: '1.7',
          color: '#333',
          background: '#FFFFFF'
        }}>
          {/* Summary */}
          <div style={{
            marginBottom: '16px',
            padding: '14px',
            background: theme.background,
            borderRadius: '10px',
            borderLeft: `4px solid ${theme.primary}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
          }}>
            <p style={{ 
              margin: 0, 
              fontWeight: 600,
              color: theme.text,
              fontSize: '14px',
              lineHeight: '1.6'
            }}>
              {summary}
            </p>
          </div>

          {/* Strengths */}
          {strengths && strengths.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '13px',
                fontWeight: 700,
                color: '#10B981',
                marginBottom: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span style={{ fontSize: '16px' }}>✓</span>
                <span>Strengths</span>
              </div>
              <div style={{
                background: '#ECFDF5',
                borderRadius: '8px',
                padding: '12px 16px',
                border: '1px solid #10B98120'
              }}>
                <ul style={{
                  margin: 0,
                  paddingLeft: '0',
                  listStyle: 'none'
                }}>
                  {strengths.map((strength, idx) => (
                    <li key={`item-${idx}`} style={{
                      marginBottom: idx < strengths.length - 1 ? '8px' : '0',
                      paddingLeft: '0',
                      color: '#065F46',
                      fontSize: '13px',
                      lineHeight: '1.6',
                      fontWeight: 500
                    }}>
                      {strength}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Improvements */}
          {improvements && improvements.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{
                fontSize: '13px',
                fontWeight: 700,
                color: '#F7941E',
                marginBottom: '10px',
                textTransform: 'uppercase',
                letterSpacing: '0.8px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}>
                <span style={{ fontSize: '16px' }}>⚡</span>
                <span>How to Improve</span>
              </div>
              <div style={{
                background: '#FFF7ED',
                borderRadius: '8px',
                padding: '12px 16px',
                border: '1px solid #F7941E20'
              }}>
                <ul style={{
                  margin: 0,
                  paddingLeft: '0',
                  listStyle: 'none'
                }}>
                  {improvements.map((improvement, idx) => (
                    <li key={`item-${idx}`} style={{
                      marginBottom: idx < improvements.length - 1 ? '8px' : '0',
                      paddingLeft: '0',
                      color: '#7C2D12',
                      fontSize: '13px',
                      lineHeight: '1.6',
                      fontWeight: 500
                    }}>
                      {improvement}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {/* Encouragement */}
          {encouragement && (
            <div style={{
              padding: '14px',
              background: `linear-gradient(135deg, ${theme.background} 0%, ${theme.primary}10 100%)`,
              borderRadius: '10px',
              border: `2px solid ${theme.primary}30`,
              fontSize: '14px',
              fontWeight: 600,
              color: theme.text,
              lineHeight: '1.6',
              fontStyle: 'italic',
              textAlign: 'center'
            }}>
              {encouragement}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AICoachInsight;
